// Fetching a monster's saves from the three sources (Feature 2, 2026-10-18).
//
// The pure resolver (monsterSave.ts) decides WHICH source to roll with; this is
// the half that actually asks. Each source is a bridge lane that answers in the
// one shared shape (MonsterSavesResult): D&D Beyond reads a monster stat block,
// the Compass answers from its bestiary, Roll20 reads the token's npc_<key>_save
// attribs. We ask every CAPABLE source at once and hand the resolver whatever
// came back, tagged by source.
//
// UNKNOWN versus NONE, the same distinction creatureActions.ts guards: a source
// the connector cannot answer (no capability) is skipped, not reported as empty;
// a source that answered but did not hold the creature is found:false; only an
// answer with real numbers is found:true. A creature nothing knew yet is not
// cached, so it is asked again once it joins the fight.
import { bridgeCapabilities, newRequestId, request } from "../bridge";
import type { MonsterSavesResult } from "./laneResults";
import { bareName } from "./creatureActions";
import type { MonsterSaveData, SaveSource } from "./monsterSave";

interface LaneSpec { source: SaveSource; cap: string; type: string; resultType: string }
const LANES: LaneSpec[] = [
  { source: "ddb", cap: "ddbMonsterSaves", type: "DM_SCREEN_DDB_MONSTER_SAVES_REQUEST", resultType: "DM_SCREEN_DDB_MONSTER_SAVES_RESULT" },
  { source: "lmcc", cap: "lmcMonsterSaves", type: "DM_SCREEN_LMC_MONSTER_SAVES_REQUEST", resultType: "DM_SCREEN_LMC_MONSTER_SAVES_RESULT" },
  { source: "roll20", cap: "roll20MonsterSaves", type: "DM_SCREEN_ROLL20_MONSTER_SAVES_REQUEST", resultType: "DM_SCREEN_ROLL20_MONSTER_SAVES_RESULT" },
];

const has = (o?: Partial<Record<string, number>>) => !!o && Object.keys(o).length > 0;

/** A lane's answer → the resolver's numeric view. A refused or empty answer is
 *  found:false; only real per-ability numbers make it found:true. */
function toData(source: SaveSource, r: MonsterSavesResult | null): MonsterSaveData {
  if (!r || !r.ok || r.found === false) return { source, found: false };
  if (!has(r.saves) && !has(r.abilities)) return { source, found: false };
  return { source, found: true, saves: r.saves, abilities: r.abilities };
}

const CACHE = new Map<string, MonsterSaveData[]>();

/** Ask every capable source for a creature's saves, in parallel, and return one
 *  MonsterSaveData per source (absent/failed sources included as found:false so
 *  the caller can see which were tried). Pass to resolveSave/candidatesFor. */
export async function monsterSaveData(name: string): Promise<MonsterSaveData[]> {
  const key = bareName(name);
  if (!key) return [];
  const cached = CACHE.get(key);
  if (cached) return cached;
  const caps = bridgeCapabilities();
  const out = await Promise.all(
    LANES.map(async (lane): Promise<MonsterSaveData> => {
      if (!caps[lane.cap]) return { source: lane.source, found: false };
      const r = await request<MonsterSavesResult>(
        { type: lane.type, requestId: newRequestId("monster-saves"), name },
        { resultType: lane.resultType, timeoutMs: 10000 },
      ).catch(() => null);
      return toData(lane.source, r);
    }),
  );
  // Cache only once something answered with data — an all-absent result is left
  // uncached so a creature that later joins Roll20 (or the Compass) is re-asked.
  if (out.some((d) => d.found)) CACHE.set(key, out);
  return out;
}

export const __clearMonsterSaveCache = () => CACHE.clear();
